import {useEffect, useState} from "react";
import {BrowserRouter, useRoutes} from "react-router-dom";
import NewHome from "../pages/NewHome";
import NotFound from "../pages/NotFound";
import AllChannels from "./../pages/AllChannels";
import {Provider} from "react-redux";
import {store} from "../stores/store";
import {AppState, AppStateService, VideoQualityLevelPreference} from "../services/appStateService";
import {AppMainStateContext} from "../contexts/AppMainStateContext.tsx";
import AuthProvider from "../providers/AuthProvider.tsx";
import {Orientation} from "../consts/orientation.ts";
import ChannelsProvider from "../providers/ChannelsProvider.tsx";
import RootLayout from "./layouts/RootLayout.tsx";
import {DeviceSize} from "../consts/device.ts";
import NewEpgPage from "../pages/NewEpgPage.tsx";
import {NotificationStatus} from "./notification/Notification.tsx";
import {NotificationProvider} from "../hooks/useNotification.tsx";
import {PlayerPage} from "../pages/PlayerPage.tsx";
import SearchPage from "../pages/SearchPage.tsx";
import NewChannelDetailsPage from "../pages/NewChannelDetailsPage.tsx";
import ChangePasswordPage from "../pages/ChangePasswordPage.tsx";
import RegisterPage from "../pages/RegisterPage.tsx";
import ConfirmRegistrationPage from "../pages/ConfirmRegistrationPage.tsx";
import LoginPage from "../pages/LoginPage.tsx";
import AccountPage from "../pages/Account/AccountPage.tsx";
import AccountChangePasswordPage from "../pages/Account/AccountChangePasswordPage.tsx";
import AccountPlayerSettingsPage from "../pages/Account/AccountPlayerSettingsPage.tsx";
import RecoverPasswordPage from "../pages/RecoverPasswordPage.tsx";
import {AccountManagementRolePage} from "../pages/Account/Admin/AccountManagementRolePage.tsx";
import {AccountManagementRoleUserPage} from "../pages/Account/Admin/AccountManagementRoleUserPage.tsx";
import {AccountManagementBannersPage} from "../pages/Account/Marketing/AccountManagementBannersPage.tsx";
import PrivacyPolicyPage from "../pages/PrivacyPolicy.tsx";
import TermsAndConditionsPage from "../pages/TermsAndConditions.tsx";
import AccountPromotionBannersEditPage from "./account/marketing/banner/AccountPromotionBannersEditPage.tsx";
import ContactPage from "../pages/ContactPage.tsx";
import {GoogleAnalyticsProvider} from "../providers/GoogleAnalyticsProvider.tsx";
import {AccountRecordingManagementPage} from "../pages/Account/AccountRecordingManagementPage.tsx";
import {ReminderProvider} from "../providers/ReminderProvider.tsx";
import {MaintenancePage} from "../pages/MaintenancePage.tsx";
import {RnDProvider} from "../providers/RnDProvider.tsx";
import {AccountManagementAdminPanelPage} from "../pages/Account/Admin/AccountManagementAdminPanelPage.tsx";
import AccountFavoritesPage from "../pages/Account/AccountFavoritesPage.tsx";
import AccountProvider from "../providers/Account/AccountProvider.tsx";
import ModalProvider from "../providers/ModalProvider.tsx";
import AccountRecordingsProvider from "../providers/Account/AccountRecordingsProvider.tsx";
import AccountFavoritesProvider from "../providers/Account/AccountFavoritesProvider.tsx";
import FirebaseProvider from "../providers/FirebaseProvider.tsx";

const Routes = () => {
    return useRoutes([
        {path: "*", element: <NotFound/>},
        {path: "/", element: <NewHome/>},
        {path: "/kanaly-tv/", element: <AllChannels/>},
        {path: "/kanaly-tv/:ch_id", element: <NewChannelDetailsPage/>},
        {path: "/kanaly-tv/:ch_id/player", element: <PlayerPage/>},
        {path: "/program-tv", element: <NewEpgPage/>},
        {path: "/szukaj", element: <SearchPage/>},
        // auth
        {path: "/aktywacja-konta/:token", element: <ConfirmRegistrationPage/>},
        {path: "/logowanie", element: <LoginPage/>},
        {path: "/odzyskiwanie-hasla", element: <RecoverPasswordPage/>},
        {path: "/rejestracja/:token", element: <RegisterPage/>},
        {path: "/zmiana-hasla/:token", element: <ChangePasswordPage/>},
        // account
        {path: "/konto", element: <AccountPage/>},
        {path: "/konto/zmiana-hasla", element: <AccountChangePasswordPage/>},
        {path: "/konto/odtwarzacz", element: <AccountPlayerSettingsPage/>},
        {path: "/konto/nagrania", element: <AccountRecordingManagementPage/>},
        {path: "/konto/ulubione", element: <AccountFavoritesPage/>},
        // account - admin
        {path: "/konto/uprawnienia", element: <AccountManagementRolePage/>},
        {path: "/konto/uprawnienia/uzytkownik/:user_id", element: <AccountManagementRoleUserPage/>},
        {path: "/konto/panel-administracyjny", element: <AccountManagementAdminPanelPage/>},
        // account - marketing
        {path: "/konto/banery", element: <AccountManagementBannersPage/>},
        {path: "/konto/banery/:banner_id", element: <AccountPromotionBannersEditPage/>},
        // other pages
        {path: "/polityka-prywatnosci", element: <PrivacyPolicyPage/>},
        {path: "/regulamin", element: <TermsAndConditionsPage/>},
        {path: "/kontakt", element: <ContactPage/>},
        {path: "/przerwa-technicza", element: <MaintenancePage/>},
    ]);
};

function App() {
    const _appStateService = new AppStateService();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [orientation, setOrientation] = useState<Orientation>(
        _appStateService.getOrientation()
    );
    const [appStates, setAppStates] = useState<AppState>(
        _appStateService.get()
    );
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDisabledCloseModal, setIsDisabledCloseModal] = useState(false);
    const [isNotificationVisible, setIsNotificationVisible] = useState(false);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
    const [notificationData, setNotificationData] = useState({
        description: "",
        status: NotificationStatus.info,
        title: "",
    });
    const [windowWidth, setWindowWidth] = useState<DeviceSize>(
        _appStateService.getDeviceSize()
    );
    const closeModal = () => {
        const body = document.querySelector("body") as HTMLElement;
        body.classList.remove("overflow-hidden")
        setIsModalOpen(false);
        setIsLoginModalOpen(false);
        setIsDisabledCloseModal(false);
    }
    const openModal = () => {
        const body = document.querySelector("body") as HTMLElement;
        body.classList.add("overflow-hidden")
        setIsModalOpen(true);
    }

    const openLoginModal = (closeModalDisabled: boolean = false) => {
        const body = document.querySelector("body") as HTMLElement;
        body.classList.add("overflow-hidden")
        setIsLoginModalOpen(true);
        setIsModalOpen(true);
        setIsDisabledCloseModal(closeModalDisabled);
    }

    useEffect(() => {
        const result = _appStateService.get();
        setIsCookiesAgreed(result.isCookiesAgreed);
        setVideoQualityPreference(result.qualityLevelPreference);
        window.addEventListener("resize", handleWindowSizeChange);
        window.addEventListener("orientationchange", handleOrientationChange);
        return () => {
            window.removeEventListener("resize", handleWindowSizeChange);
            window.removeEventListener("orientationchange", handleOrientationChange);
        };
    }, []);

    const handleWindowSizeChange = () => {
        setWindowWidth(_appStateService.getDeviceSize());
        setIsMobile(window.innerWidth <= DeviceSize.tabletL);
    };
    const handleOrientationChange = () => {
        setOrientation(_appStateService.getOrientation());
    };
    const setIsCookiesAgreed = (isAgreed: boolean | "not set") => {
        _appStateService.setIsCookiesAgreed(isAgreed);
        setAppStates(_appStateService.get());
    };
    const setVideoQualityPreference = (videoQualityLevel: VideoQualityLevelPreference) => {
        _appStateService.setQualityLevelPreference(videoQualityLevel);
        setAppStates(_appStateService.get());
    };


    return (
        <BrowserRouter>
            <AppMainStateContext.Provider
                value={{
                    isMobile,
                    isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent),
                    appStates,
                    isModalOpen,
                    isSearchModalOpen,
                    isDisabledCloseModal,
                    isLoginModalOpen,
                    isNotificationVisible,
                    notificationData,
                    orientation,
                    windowWidth,
                    setIsCookiesAgreed,
                    setVideoQualityPreference,
                    setIsSearchModalOpen,
                    setIsNotificationVisible,
                    setNotificationData,
                    closeModal,
                    openModal,
                    openLoginModal
                }}
            >
                <GoogleAnalyticsProvider>
                    <NotificationProvider>
                        <ChannelsProvider>
                            <AuthProvider>
                                <AccountProvider>
                                    <RnDProvider>
                                        <Provider store={store}>
                                            <RootLayout>
                                                <ReminderProvider>
                                                    <AccountFavoritesProvider>
                                                        <AccountRecordingsProvider>
                                                            {/*<ReverbProvider>*/}
                                                                <FirebaseProvider>
                                                                    <ModalProvider>
                                                                        <Routes/>
                                                                    </ModalProvider>
                                                                </FirebaseProvider>
                                                            {/*</ReverbProvider>*/}
                                                        </AccountRecordingsProvider>
                                                    </AccountFavoritesProvider>
                                                </ReminderProvider>
                                            </RootLayout>
                                        </Provider>
                                    </RnDProvider>
                                </AccountProvider>
                            </AuthProvider>
                        </ChannelsProvider>
                    </NotificationProvider>
                </GoogleAnalyticsProvider>
            </AppMainStateContext.Provider>
        </BrowserRouter>
    );
}

export default App;
